/**
 * Lift2Fit — block goals.
 *
 * Add up to five goals for the current block, edit a target, delete one. The
 * metric is a plain-English select and, for an e1RM goal, an exercise picker.
 * A live percentage bar updates as you type.
 *
 * Mutations go to the API when it answers. In demo mode they are applied
 * locally (the tab is the database) and the screen says so; a 409 from the
 * 5-goal cap is always surfaced as a friendly inline message, never a crash.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { api, type Goal, type GoalInput, type GoalMetric, type LifterDashboard } from '@/lib/api';
import { uid } from '@/lib/format';
import { useSession } from '@/lib/useSession';
import { Button } from '@/components/ui';
import { ArrowRightIcon, GridIcon, PlusIcon, TargetIcon, TrashIcon } from '@/lib/icons';
import { EmptyState, LiveMessage, LoadingPanel, Panel, ScreenHeader } from '../components';
import { GoalProgressBar, SourceNotice } from '../blocks';
import { GOAL_METRICS, GOAL_METRIC_HELP, GOAL_METRIC_LABELS, GOAL_METRIC_UNITS, MAX_BLOCK_GOALS, goalProgressPct } from '../demoData';
import { invalidate } from '@/lib/readCache';
import { useAsync, useCachedAsync } from '../hooks';
import { Link } from '../router';

const CAP_MESSAGE = `This block already has ${MAX_BLOCK_GOALS} goals — delete one before adding another.`;

/**
 * Whether the dashboard carries evidence for this metric right now. Pain is the
 * only metric that can be absent: `painScore` is null until a set with a pain
 * score is logged, and an absent reading must not preview as "achieved".
 */
function metricHasData(metric: GoalMetric, painScore: number | null): boolean {
  if (metric !== 'pain_average') return true;
  return painScore !== null;
}

/**
 * The goal capability itself, hosted by both the `/goals` route and the
 * Manage coach page's Goals tab. A goal is the lifter's own: it needs no coach,
 * and a connected coach reads it only through the shared-fields gate.
 */
export function GoalsManager() {
  const session = useSession();
  const track = session.user?.track ?? 'general';
  const blockState = useCachedAsync(`blocks/current:${track}`, () => api.blocksCurrent(), [track]);
  const dashboardState = useCachedAsync(`me/dashboard:${track}`, () => api.dashboard('lifter'), [track]);
  const exercisesState = useCachedAsync('exercises', () => api.exercises(), [track]);

  const blockData = blockState.data;
  const dashboard: LifterDashboard | null =
    dashboardState.data && dashboardState.data.role === 'lifter' ? dashboardState.data : null;
  const metrics = dashboard?.trackMetrics ?? null;
  const exercises = exercisesState.data ?? [];

  const [goals, setGoals] = useState<Goal[]>([]);
  const [title, setTitle] = useState('');
  const [metric, setMetric] = useState<GoalMetric>('sessions');
  const [target, setTarget] = useState('');
  const [exerciseId, setExerciseId] = useState('');
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [tone, setTone] = useState<'info' | 'success' | 'warning' | 'danger'>('info');

  // The payload is the source of truth; local state carries optimistic edits.
  useEffect(() => {
    if (blockData) setGoals(blockData.goals);
  }, [blockData]);

  // Default the exercise picker to the first option the moment it is needed.
  useEffect(() => {
    if (metric === 'exercise_1rm' && !exerciseId && exercises.length > 0) {
      setExerciseId(exercises[0]?.id ?? '');
    }
  }, [metric, exerciseId, exercises]);

  function currentFor(selected: GoalMetric): number {
    if (selected === 'sessions') return blockData?.totals.sessions ?? 0;
    if (selected === 'sets') return blockData?.totals.sets ?? 0;
    if (selected === 'volume_kg') return blockData?.totals.volumeKg ?? 0;
    if (selected === 'bodyweight_kg') return session.user?.bodyweightKg ?? metrics?.bodyweightKg ?? 0;
    if (selected === 'exercise_1rm') return metrics?.bestE1rmKg ?? 0;
    if (selected === 'mobility_sessions') return metrics?.mobilitySessions ?? 0;
    if (selected === 'pain_average') return metrics?.painScore ?? 0;
    return metrics?.intervalMinutes ?? 0;
  }

  const parsedTarget = Number(target);
  const previewCurrent = currentFor(metric);
  // The dashboard's pain reading is a 7-day average; the goal's own value comes
  // from the whole block once it exists. This is a preview only, but it must not
  // invent pain: a null reading shows as "no pain logged yet", not a free pass.
  const previewHasData = metricHasData(metric, metrics?.painScore ?? null);
  const previewGoal: Goal = {
    id: 'preview',
    title: title.trim() || 'Your new goal',
    metric,
    target: Number.isFinite(parsedTarget) ? parsedTarget : 0,
    current: previewCurrent,
    unit: GOAL_METRIC_UNITS[metric],
    progressPct: goalProgressPct(metric, previewCurrent, parsedTarget, previewHasData),
    status: 'active',
    dueOn: blockData?.block.endsOn ?? new Date().toISOString(),
    exerciseId: metric === 'exercise_1rm' ? exerciseId || null : null,
  };

  const atCap = goals.length >= MAX_BLOCK_GOALS;

  const addGoal = async (event: FormEvent) => {
    event.preventDefault();
    setMessage(null);
    const trimmed = title.trim();
    if (trimmed.length < 2) {
      setTone('danger');
      setMessage('Give the goal a short title.');
      return;
    }
    const value = Number(target);
    if (!Number.isFinite(value) || value <= 0) {
      setTone('danger');
      setMessage('Enter a target above zero.');
      return;
    }
    const chosenExercise = exerciseId || exercises[0]?.id || '';
    if (metric === 'exercise_1rm' && !chosenExercise) {
      setTone('danger');
      setMessage('Pick the exercise this e1RM goal tracks.');
      return;
    }
    if (atCap) {
      setTone('warning');
      setMessage(CAP_MESSAGE);
      return;
    }

    const input: GoalInput = {
      title: trimmed,
      metric,
      target: value,
      unit: GOAL_METRIC_UNITS[metric],
      ...(metric === 'exercise_1rm' && chosenExercise ? { exerciseId: chosenExercise } : {}),
    };
    const optimisticProgress = goalProgressPct(metric, previewCurrent, value, previewHasData);
    const optimistic: Goal = {
      id: uid('goal'),
      title: trimmed,
      metric,
      target: value,
      current: previewCurrent,
      unit: GOAL_METRIC_UNITS[metric],
      progressPct: optimisticProgress,
      status: optimisticProgress >= 100 ? 'achieved' : 'active',
      dueOn: blockData?.block.endsOn ?? new Date().toISOString(),
      exerciseId: metric === 'exercise_1rm' ? chosenExercise : null,
    };

    setGoals((prev) => [...prev, optimistic]);
    setTitle('');
    setTarget('');
    setExerciseId('');
    setBusy(true);
    // instant paint: a goal write invalidates the reads that show goals.
    invalidate('me/goals');
    const result = await api.createGoal(input);
    setBusy(false);

    const created = result.data;
    if (created) {
      setGoals((prev) => prev.map((goal) => (goal.id === optimistic.id ? created : goal)));
      setTone('success');
      setMessage(
        result.source === 'demo'
          ? `Added “${trimmed}” in this tab — demo mode does not save to a server.`
          : `Added “${trimmed}”.`,
      );
      return;
    }

    // A real problem came back (the cap is a 409): roll the optimistic add back.
    setGoals((prev) => prev.filter((goal) => goal.id !== optimistic.id));
    setTone('warning');
    setMessage(result.problem?.status === 409 ? CAP_MESSAGE : result.problem?.detail ?? 'Could not add that goal.');
  };

  const saveTarget = async (goal: Goal) => {
    const value = Number(drafts[goal.id] ?? String(goal.target));
    if (!Number.isFinite(value) || value <= 0) {
      setTone('danger');
      setMessage('Enter a target above zero.');
      return;
    }
    setBusyId(goal.id);
    // instant paint: a goal write invalidates the reads that show goals.
    invalidate('me/goals');
    const result = await api.updateGoal(goal.id, { target: value });
    setBusyId(null);
    const updated = result.data;
    if (updated) {
      setGoals((prev) => prev.map((candidate) => (candidate.id === goal.id ? updated : candidate)));
      setTone('success');
      setMessage(`Updated “${goal.title}”.`);
      return;
    }
    setTone('danger');
    setMessage(result.problem?.detail ?? 'Could not update that goal.');
  };

  const removeGoal = async (goal: Goal) => {
    setBusyId(goal.id);
    // instant paint: a goal write invalidates the reads that show goals.
    invalidate('me/goals');
    const result = await api.deleteGoal(goal.id);
    setBusyId(null);
    if (result.data) {
      setGoals((prev) => prev.filter((candidate) => candidate.id !== goal.id));
      setTone('success');
      setMessage(`Deleted “${goal.title}”.`);
      return;
    }
    setTone('danger');
    setMessage(result.problem?.detail ?? 'Could not delete that goal.');
  };

  if (blockState.loading && !blockData) {
    return (
      <div className="flex flex-col gap-4">
        <LoadingPanel label="Loading your block goals" rows={4} />
        <LoadingPanel label="Loading your numbers" rows={3} />
      </div>
    );
  }

  if (!blockData) {
    return (
      <EmptyState
        title="We couldn't load your block"
        body={
          blockState.error ??
          'The API did not answer. Your own goals were not replaced with demo numbers — try again.'
        }
        icon={<TargetIcon className="h-8 w-8 text-accent" />}
        action={
          <div className="flex flex-wrap gap-2">
            <Button onClick={blockState.reload} data-testid="goals-retry">
              Try again
            </Button>
            <Link
              to="/summary"
              className="inline-flex h-11 items-center gap-2 border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
            >
              Block summary
              <ArrowRightIcon />
            </Link>
          </div>
        }
      />
    );
  }

  const emptyBlock = blockData.totals.sessions === 0 && blockData.totals.sets === 0;

  return (
    <div className="flex flex-col gap-6" data-testid="goals-manager">
      {blockState.source === 'demo' ? (
        <p data-testid="goals-demo-note" className="text-[12px] text-ink-muted">
          Demo mode — the demo goals load from the built-in dataset. Anything you add or edit here stays in this tab
          until the API is reachable; the banner above always tells you which one you are on.
        </p>
      ) : null}

      <SourceNotice source={blockState.source} isDemoSession={session.isDemo} />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
        <Panel title="Add a goal" subtitle="The bar below moves as you type the target.">
          <form className="flex flex-col gap-4" onSubmit={addGoal} noValidate>
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Title</span>
              <input
                type="text"
                name="title"
                data-testid="goal-title-input"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Squat 150 kg e1RM"
                className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
              />
            </label>

            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Metric</span>
              <select
                name="metric"
                data-testid="goal-metric-select"
                value={metric}
                onChange={(event) => setMetric(event.target.value as GoalMetric)}
                className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
              >
                {GOAL_METRICS.map((option) => (
                  <option key={option} value={option}>
                    {GOAL_METRIC_LABELS[option]}
                  </option>
                ))}
              </select>
              <span data-testid="goal-metric-help" className="text-[12px] leading-snug text-ink-muted">
                {GOAL_METRIC_HELP[metric]}
              </span>
            </label>

            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">
                Target ({metric === 'pain_average' ? 'average ceiling, 0–10' : GOAL_METRIC_UNITS[metric]})
              </span>
              <input
                type="number"
                name="target"
                inputMode="decimal"
                min={0}
                step="any"
                data-testid="goal-target-input"
                value={target}
                onChange={(event) => setTarget(event.target.value)}
                placeholder="150"
                className="h-11 border border-hairline bg-page px-3 font-mono text-[15px] text-ink"
              />
            </label>

            {metric === 'exercise_1rm' ? (
              <label className="flex flex-col gap-1.5">
                <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Exercise</span>
                <select
                  name="exercise"
                  data-testid="goal-exercise-select"
                  value={exerciseId}
                  onChange={(event) => setExerciseId(event.target.value)}
                  className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
                >
                  <option value="">Choose an exercise</option>
                  {exercises.map((exercise) => (
                    <option key={exercise.id} value={exercise.id}>
                      {exercise.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}

            <div className="flex flex-col gap-1.5 border-t border-hairline pt-3">
              <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Live progress</span>
              <ul className="flex flex-col">
                <GoalProgressBar goal={previewGoal} testId="goal-preview" />
              </ul>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button type="submit" data-testid="goal-add" disabled={busy || atCap}>
                <PlusIcon className="text-sm" />
                {busy ? 'Adding…' : 'Add goal'}
              </Button>
              {atCap ? (
                <span data-testid="goal-cap-note" className="text-[12px] text-warning">
                  {CAP_MESSAGE}
                </span>
              ) : (
                <span className="text-[12px] text-ink-muted">
                  {MAX_BLOCK_GOALS - goals.length} of {MAX_BLOCK_GOALS} goal slots free.
                </span>
              )}
            </div>
            <LiveMessage message={message} tone={tone} />
          </form>
        </Panel>

        <Panel
          title="Goals this block"
          subtitle={`${goals.length} of ${MAX_BLOCK_GOALS} set. Edit a target in place or delete a goal.`}
          actions={
            <Link
              to="/summary"
              className="inline-flex h-9 items-center gap-2 border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
            >
              <GridIcon className="text-[13px]" />
              See the bars
            </Link>
          }
        >
          {emptyBlock && goals.length === 0 ? (
            <div data-testid="goals-empty">
              <EmptyState
                title="No sessions yet in this block"
                body="Log your first set and every goal bar starts moving. You can still add a goal now — targets are saved against this block."
                icon={<TargetIcon className="h-8 w-8 text-accent" />}
                action={
                  <Link
                    to="/grid"
                    className="inline-flex h-11 items-center gap-2 border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
                  >
                    <GridIcon className="text-sm" />
                    Log your first set
                  </Link>
                }
              />
            </div>
          ) : null}

          {goals.length ? (
            <ul className="flex flex-col">
              {goals.map((goal) => (
                <GoalProgressBar
                  key={goal.id}
                  goal={goal}
                  actions={
                    <span className="flex flex-wrap items-center gap-2">
                      <label className="flex items-center gap-1.5">
                        <span className="sr-only">Target for {goal.title}</span>
                        <input
                          type="number"
                          inputMode="decimal"
                          min={0}
                          step="any"
                          data-testid="goal-target-edit"
                          aria-label={`Target for ${goal.title}`}
                          value={drafts[goal.id] ?? String(goal.target)}
                          onChange={(event) =>
                            setDrafts((prev) => ({ ...prev, [goal.id]: event.target.value }))
                          }
                          className="h-9 w-24 border border-hairline bg-page px-2 font-mono text-[13px] text-ink"
                        />
                      </label>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void saveTarget(goal)}
                        disabled={busyId === goal.id}
                      >
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label={`Delete goal ${goal.title}`}
                        onClick={() => void removeGoal(goal)}
                        disabled={busyId === goal.id}
                        className="text-danger"
                      >
                        <TrashIcon className="text-sm" />
                      </Button>
                    </span>
                  }
                />
              ))}
            </ul>
          ) : emptyBlock ? null : (
            <p className="text-[13px] text-ink-muted">No goals yet for this block. Add the first one on the left.</p>
          )}
        </Panel>
      </div>
    </div>
  );
}

/** The `/goals` route: the shared goal manager under the block-goals header. */
export function GoalsScreen() {
  return (
    <div className="flex flex-col gap-6">
      <ScreenHeader
        eyebrow="Block goals"
        title="Goals"
        lead={`Up to ${MAX_BLOCK_GOALS} goals for the current block, each tracked against your own log. No coach required — a connected coach reads them through your shared fields.`}
        actions={
          <Link
            to="/summary"
            className="inline-flex h-11 items-center gap-2 border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
          >
            Block summary
            <ArrowRightIcon />
          </Link>
        }
      />
      <GoalsManager />
    </div>
  );
}
